import { createContext, useContext, useEffect } from 'react'

export interface IShortcutListItem {
  id: string
  label: string
  /** Alternative ways to press it, each a chord of keycaps left to right. */
  chords: string[][]
}

export interface IShortcutListSection {
  title: string
  shortcuts: IShortcutListItem[]
}

type Unregister = () => void

interface IAppShortcutsContext {
  openShortcutList: () => void
  registerPageShortcuts: (section: IShortcutListSection) => Unregister
  registerNewTaskCompany: (companyId: string) => Unregister
}

const noop = (): void => undefined
const registerNothing = (): Unregister => noop

/** Pages rendered outside `AppShortcutsProvider` (blank layouts, tests) register into nothing. */
export const AppShortcutsContext = createContext<IAppShortcutsContext>({
  openShortcutList: noop,
  registerPageShortcuts: registerNothing,
  registerNewTaskCompany: registerNothing,
})

export const useAppShortcuts = (): IAppShortcutsContext => useContext(AppShortcutsContext)

/** Lists this surface's own shortcuts on the shortcut sheet for as long as it is mounted.
 *  Pass a module-level constant: a new object each render re-registers it. */
export const usePageShortcuts = (section: IShortcutListSection): void => {
  const { registerPageShortcuts } = useAppShortcuts()
  useEffect(() => registerPageShortcuts(section), [registerPageShortcuts, section])
}

/** The company a New Task pop-up opened from this page fills in. */
export const useNewTaskCompany = (companyId: string | null | undefined): void => {
  const { registerNewTaskCompany } = useAppShortcuts()
  useEffect(
    () => (companyId ? registerNewTaskCompany(companyId) : undefined),
    [registerNewTaskCompany, companyId],
  )
}
